/* eslint-disable import/no-named-as-default-member */
import { yupResolver } from "@hookform/resolvers/yup";
import {
  FormControlLabel,
  GKButton,
  GKDatePicker,
  GKModal,
  GKRadio,
  GKSelect,
  Grid2,
  MenuItem,
  RadioGroup,
  ReloadIcon,
  SendIcon,
  Typography,
} from "components";
import dayjs from "dayjs";
import { useMutation } from "hooks/useQueries";
import { Controller, useForm } from "react-hook-form";
import axiosInstance from "utils/axiosInstance";
import { GKModalProps } from "utils/commonInterface";
import { KYC_OPTIONS } from "utils/constants";
import { clientKycModalFormSchema } from "utils/validationSchema";

interface Props {
  showData?: any;
  refetch?: () => void;
  clientData?: any;
  portfolioItem?: any;
  isPortfolio?: any;
}

type FormValues = {
  pan?: string;
  identification_type?: string;
  kyc_status?: string;
  sendOptions?: string;
  dateOfBirth?: string | null;
};

const KYCModal = ({
  setOpen,
  open,
  showData,
  refetch,
  clientData,
  portfolioItem,
  isPortfolio,
}: Props & GKModalProps) => {
  const client = clientData?.id;

  const portfolio = portfolioItem?.id;

  const { mutate: createKyc, isLoading: createLoading } = useMutation({
    mutationKey: ["CREATE_KYC"],
    mutationFn: (data) => axiosInstance.post(`/kyc/${clientData?.id}/`, data),
    onSuccess: () => {
      refetch?.();
      setOpen(false);
    },
  });

  const { mutate: fetchKRA, isLoading: KRALoading } = useMutation({
    mutationKey: ["FETCH_KRA"],
    mutationFn: (data) => axiosInstance.post("/pan-details/", data),
    onSuccess: () => {
      refetch?.();
      setOpen(false);
    },
  });

  const form = useForm<FormValues>({
    defaultValues: {
      pan: showData?.pan || clientData?.pan || "",
      identification_type: showData?.identificationType || "",
      kyc_status: showData?.kycStatus || "",
      sendOptions: showData?.sendOptions || "Mobile",
      dateOfBirth: showData?.dateOfBirth || clientData?.dateOfBirth || null,
    },
    resolver: yupResolver(clientKycModalFormSchema),
  });

  const selectedKycType = form.watch("identification_type");

  const onSubmit = (values: FormValues) => {
    if (!client) return;

    if (selectedKycType === "KRA FETCH") {
      fetchKRA({
        pan: values.pan,
        dob: values.dateOfBirth,
        phone: clientData?.phone,
        company: clientData?.company || "",
        clientId: client,
        portfolioId: isPortfolio ? portfolio : null,
      });
    } else {
      createKyc({
        pan: values.pan,
        identificationType: values.identification_type,
        kycStatus: values.kyc_status,
        sendOptions: values.sendOptions,
        client,
        portfolio,
      });
    }
  };

  return (
    <GKModal
      width={"350px"}
      open={open}
      setOpen={setOpen}
      modalTitle={"Client KYC"}
      footer={
        <Grid2 container justifyContent="flex-end" gap={1}>
          <GKButton
            sx={{ width: "155px" }}
            variant="outlined"
            size="medium"
            onClick={() => setOpen(false)}
          >
            Cancel
          </GKButton>
          <GKButton
            sx={{ width: "155px" }}
            type="submit"
            variant="contained"
            form="kyc-form"
            size="medium"
            loading={createLoading || KRALoading}
            startIcon={
              selectedKycType === "KRA FETCH" ? <ReloadIcon /> : <SendIcon />
            }
          >
            {selectedKycType === "KRA FETCH"
              ? KRALoading
                ? "Fetching"
                : "Fetch KRA"
              : "Send Link"}
          </GKButton>
        </Grid2>
      }
    >
      <form id="kyc-form" onSubmit={form.handleSubmit(onSubmit)}>
        <Grid2 container spacing={2}>
          {/* Client Info */}
          <Grid2
            container
            size={12}
            p={1.5}
            borderRadius={2}
            bgcolor={"var(--grey-bg-100)"}
            gap={1}
          >
            <Grid2 size={12} display="flex" alignItems="flex-start">
              <Typography fontSize={14} fontWeight={500} color="#222">
                {clientData?.name || portfolioItem?.name || "-"}
              </Typography>
            </Grid2>
            <Grid2 size={12} display="flex" alignItems="center">
              <Typography
                fontSize={12}
                fontWeight={600}
                color="#697586"
                sx={{ mr: 1 }}
              >
                PAN:
              </Typography>
              <Typography fontSize={12} fontWeight={500} color="#222">
                {clientData?.pan || portfolioItem?.pan || "-"}
              </Typography>
            </Grid2>
          </Grid2>

          {/* KYC Type */}
          <Grid2 container size={12}>
            <Controller
              name="identification_type"
              control={form.control}
              render={({ field, formState: { errors } }) => (
                <GKSelect
                  {...field}
                  inputLabel="KYC Type"
                  error={Boolean(errors?.identification_type)}
                  helperText={errors?.identification_type?.message}
                >
                  <MenuItem style={{ display: "none" }} value="">
                    Select Identification Type
                  </MenuItem>
                  {KYC_OPTIONS.map((data) => (
                    <MenuItem value={data.value} key={data.value}>
                      {data.name}
                    </MenuItem>
                  ))}
                </GKSelect>
              )}
            />
          </Grid2>

          {/* Send Options */}
          {selectedKycType !== "KRA FETCH" && (
            <Grid2 size={12}>
              <Typography fontSize={14} fontWeight={500}>
                Send KYC via
              </Typography>
              <Grid2 display="flex" gap={3}>
                <Controller
                  name="sendOptions"
                  control={form.control}
                  render={({ field }) => (
                    <RadioGroup
                      {...field}
                      value={field.value || "Mobile"}
                      onChange={(e) => field.onChange(e.target.value)}
                      sx={{ display: "flex", flexDirection: "row", gap: 0.5 }}
                    >
                      <FormControlLabel
                        value="Mobile"
                        control={<GKRadio />}
                        label="Mobile"
                      />
                      <FormControlLabel
                        value="Email"
                        control={<GKRadio />}
                        label="Email"
                      />
                    </RadioGroup>
                  )}
                />
              </Grid2>
            </Grid2>
          )}

          {/* Date of Birth */}
          {selectedKycType === "KRA FETCH" && (
            <Grid2 size={12}>
              <Controller
                name="dateOfBirth"
                control={form.control}
                render={({ field }) => (
                  <GKDatePicker
                    {...field}
                    inputLabel="Date of Birth"
                    value={field.value ? dayjs(field.value) : null}
                    onChange={(value) =>
                      field.onChange(
                        value ? dayjs(value).format("YYYY-MM-DD") : "",
                      )
                    }
                    minDate={dayjs("1901-01-01")}
                    slotProps={{
                      textField: { error: false, helperText: false },
                    }}
                  />
                )}
              />
            </Grid2>
          )}
        </Grid2>
      </form>
    </GKModal>
  );
};

export default KYCModal;
